import React, { useEffect, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { ArrowLeftOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Skeleton, Empty, Alert } from "antd";
import moment from "moment";
import history from "../../routes/history";
import { canUseSubscriptionFeatures } from "../../shared/billing";
import BuyCreditsModal from "../../components/BuyCreditsModal/BuyCreditsModal";
import ManageSubscriptionModal from "../../components/ManageSubscriptionModal/ManageSubscriptionModal";
import CreditUsagePanel from "./components/CreditUsagePanel";
import CreditHistoryTable from "./components/CreditHistoryTable";
import {
  fetchCreditTransactionsRequested,
  fetchCreditsBalanceRequested,
  fetchPlansRequested,
} from "../../redux/actions";
import "./TransactionHistory.css";

const PAGE_SIZE = 10;
const MAX_VISIBLE_PAGES = 5;

const isPlanExpired = (creditsBalance) => {
  const p = creditsBalance?.data && typeof creditsBalance.data === "object" ? creditsBalance.data : creditsBalance || {};
  if (p?.hasActivePlan === false) return true;
  const s = (p?.status || "").trim().toLowerCase().replace(/^['"]+|['"]+$/g, "");
  return ["expired", "cancelled", "canceled"].includes(s);
};

const getAvailableCredits = (creditsBalance) =>
  Number(
    (creditsBalance?.data && typeof creditsBalance.data === "object"
      ? creditsBalance.data
      : creditsBalance || {}
    )?.totalCreditsBalance
  ) || 0;

const getUsageMetrics = (creditsBalance) => {
  const balancePayload =
    creditsBalance?.data && typeof creditsBalance.data === "object"
      ? creditsBalance.data
      : creditsBalance || {};
  const remainingCredits = Number(balancePayload?.totalCreditsBalance) || 0;
  const grantedPlanCredits = Number(balancePayload?.creditsGranted) || 0;
  const topupCredits = Number(balancePayload?.topupCreditsBalance) || 0;
  const totalCredits = Math.max(
    remainingCredits,
    grantedPlanCredits + topupCredits
  );

  return {
    remainingCredits,
    totalCredits,
  };
};

const formatTransactionAction = (transaction = {}) => {
  const metadata = transaction?.metadata || {};

  return (
    metadata?.packCode ||
    metadata?.planCode ||
    metadata?.productCode ||
    transaction?.transactionType ||
    "-"
  );
};

const getVisiblePages = (currentPage, totalPages) => {
  let startPage = Math.max(1, currentPage - Math.floor(MAX_VISIBLE_PAGES / 2));
  let endPage = startPage + MAX_VISIBLE_PAGES - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
  }

  const pages = [];

  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }

  return pages;
};

const mapTransactionToRow = (transaction) => ({
  id: transaction._id,
  date: moment(transaction.createdAt).format("MMM DD, YYYY"),
  member: {
    name: transaction.performedByAccountName || "-",
    email: transaction.performedByAccountEmail || "-",
  },
  action: transaction.transactionTypeMessage || formatTransactionAction(transaction),
  credits: transaction.creditsChange ?? 0,
  remaining: transaction.balanceAfter ?? 0,
});

const TransactionHistory = () => {
  const dispatch = useDispatch();
  const subscriptionFeaturesEnabled = canUseSubscriptionFeatures();
  const [currentPage, setCurrentPage] = useState(1);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [showManageSubscriptionModal, setShowManageSubscriptionModal] = useState(false);

  const {
    loading: plansLoading,
    plans,
    plansDirty,
    creditsBalance,
    creditsBalanceLoading,
    creditsBalanceLoaded,
    transactions,
    transactionsPage,
    transactionsTotal,
    transactionsTotalPages,
    transactionsLoading,
    transactionsError,
  } = useSelector((state) => state.plansReducer, shallowEqual);
  const usageMetrics = getUsageMetrics(creditsBalance);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(
      fetchCreditTransactionsRequested({
        page,
        limit: PAGE_SIZE,
      })
    );
  };

  useEffect(() => {
    if (!subscriptionFeaturesEnabled) {
      history.replace("/myprofile");
      return;
    }

    if (!creditsBalanceLoaded) {
      dispatch(fetchCreditsBalanceRequested());
    }

    if (plansDirty && !plansLoading) {
      dispatch(fetchPlansRequested());
    }
  }, [dispatch, creditsBalanceLoaded, plansDirty, plansLoading, subscriptionFeaturesEnabled]);

  useEffect(() => {
    if (!subscriptionFeaturesEnabled) {
      return;
    }

    setCurrentPage(1);
    dispatch(
      fetchCreditTransactionsRequested({
        page: 1,
        limit: PAGE_SIZE,
      })
    );
  }, [dispatch, subscriptionFeaturesEnabled]);

  if (!subscriptionFeaturesEnabled) return null;

  const rows = Array.isArray(transactions)
    ? transactions.map(mapTransactionToRow)
    : [];
  const visiblePages = getVisiblePages(
    transactionsPage || currentPage,
    transactionsTotalPages || 1
  );

  return (
    <div className="transactionHistory-page">
      <BuyCreditsModal
        visible={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        onUpgradeToPlan={() => setShowManageSubscriptionModal(true)}
        availableCredits={getAvailableCredits(creditsBalance)}
      />
      <ManageSubscriptionModal
        visible={showManageSubscriptionModal}
        onClose={() => setShowManageSubscriptionModal(false)}
        plans={plans}
        creditsBalance={creditsBalance}
        loading={plansLoading}
      />

      <div className="transactionHistory-shell">
        <button
          className="transactionHistory-backBtn"
          type="button"
          onClick={() => history.goBack()}
        >
          <ArrowLeftOutlined />
        </button>

        <div className="transactionHistory-hero">
          <h1 className="transactionHistory-title">Credits</h1>
          <p className="transactionHistory-subtitle">
            Track your credit usage and purchase additional credits.
          </p>
        </div>

        {creditsBalanceLoading ? (
          <div className="transactionHistory-loadingState">
            <Skeleton active paragraph={{ rows: 2 }} />
          </div>
        ) : (
          <CreditUsagePanel
            remainingCredits={usageMetrics.remainingCredits}
            totalCredits={usageMetrics.totalCredits}
            onBuyMore={() => setShowBuyCreditsModal(true)}
            buyMoreDisabled={isPlanExpired(creditsBalance)}
          />
        )}

        {transactionsLoading ? (
          <div className="transactionHistory-loadingState">
            <Skeleton active paragraph={{ rows: 5 }} />
          </div>
        ) : transactionsError ? (
          <Alert
            type="error"
            message="Failed to load transaction history"
            description={transactionsError?.message || String(transactionsError)}
          />
        ) : rows.length === 0 ? (
          <Empty description="No transactions found" />
        ) : (
          <>
            <CreditHistoryTable rows={rows} />
            {transactionsTotalPages >= 1 ? (
              <div className="transactionHistory-pagination">
                <button
                  className="transactionHistory-pgBtn transactionHistory-pgArrow"
                  type="button"
                  disabled={(transactionsPage || currentPage) === 1}
                  onClick={() => handlePageChange((transactionsPage || currentPage) - 1)}
                >
                  <LeftOutlined />
                </button>

                {visiblePages[0] > 1 ? (
                  <>
                    <button
                      className="transactionHistory-pgBtn"
                      type="button"
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </button>
                    {visiblePages[0] > 2 ? (
                      <span className="transactionHistory-pgDots">...</span>
                    ) : null}
                  </>
                ) : null}

                {visiblePages.map((page) => (
                  <button
                    key={page}
                    className={`transactionHistory-pgBtn ${
                      (transactionsPage || currentPage) === page
                        ? "transactionHistory-pgActive"
                        : ""
                    }`}
                    type="button"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}

                {visiblePages[visiblePages.length - 1] < (transactionsTotalPages || 1) ? (
                  <>
                    {visiblePages[visiblePages.length - 1] < (transactionsTotalPages || 1) - 1 ? (
                      <span className="transactionHistory-pgDots">...</span>
                    ) : null}
                    <button
                      className="transactionHistory-pgBtn"
                      type="button"
                      onClick={() => handlePageChange(transactionsTotalPages || 1)}
                    >
                      {transactionsTotalPages || 1}
                    </button>
                  </>
                ) : null}

                <button
                  className="transactionHistory-pgBtn transactionHistory-pgArrow"
                  type="button"
                  disabled={(transactionsPage || currentPage) === (transactionsTotalPages || 1)}
                  onClick={() => handlePageChange((transactionsPage || currentPage) + 1)}
                >
                  <RightOutlined />
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
